import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../components/ui";
import { CRUDTooltip } from "../../../../components/base/CRUDTooltip";
import { useDarkMode } from "../../../../context/ThemeContext";
import { panelThemeClasses } from "../../../../theme/themeClasses";
import { useDisableCRUD } from "../../../../store/admin/companiesStore";
import { useVehiclesSocketStore } from "../../../../store/admin/vehiclesSocketStore";
import { useOpenChat } from "../../../../pages/support/hooks/useOpenChat";
import NewConversationModal from "../../../../pages/support/components/NewConversationModal";
import { collectTripParticipantSeed } from "../../../../pages/support/utils/tripChatHelpers";
import { useTripForm } from "../../../admin/components/sections/trips/hooks";
import type { Trip } from "../../../admin/components/sections/trips/types";
import {
  TripAddModal,
  TripAssignmentsModal,
  TripDetailsModal,
  TripEditModal,
  TripFilters,
  TripList,
  TripPagination,
} from "../../../admin/components/sections/trips/components";

export default function ViajesView() {
  const { t } = useTranslation("envios");
  const { t: tAdmin } = useTranslation("adminSections");
  const { isDarkMode } = useDarkMode();
  const disableCRUD = useDisableCRUD();
  const themeClasses = panelThemeClasses();

  const [chatTrip, setChatTrip] = useState<Trip | null>(null);
  const [assignmentsTrip, setAssignmentsTrip] = useState<Trip | null>(null);
  const socketVehicles = useVehiclesSocketStore((state) => state.vehicles);
  const chatParticipantSeed = useMemo(
    () => collectTripParticipantSeed(chatTrip, socketVehicles),
    [chatTrip, socketVehicles],
  );
  const { goToConversation } = useOpenChat();

  const {
    trips,
    meta,
    loading,
    selectedTrip,
    loadingSubmit,
    register,
    errors,
    setValue,
    watchedValues,
    showDetailsModal,
    showEditModal,
    showCreateModal,
    selectedStatus,
    searchQuery,
    selectedDeviceId,
    dateFrom,
    dateTo,
    selectedView,
    currentPage,
    handleEdit,
    handleDetails,
    handleDelete,
    handleResetTrip,
    handleOpenCreateModal,
    handleCloseModals,
    handleEditSubmit,
    handleCreateSubmit,
    handleStatusFilterChange,
    handleSearchChange,
    handleDeviceFilterChange,
    handleDateFromChange,
    handleDateToChange,
    handleClearFilters,
    setSelectedView,
    setCurrentPage,
  } = useTripForm();

  return (
    <div className="space-y-5 px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold text-content">{t("trips.title")}</h1>
          <p className="mt-1 text-sm text-content-secondary">{t("trips.subtitle")}</p>
        </div>
        <CRUDTooltip className="shrink-0">
          <Button
            size="sm"
            icon={<i className="ri-add-line" />}
            disabled={disableCRUD}
            onClick={handleOpenCreateModal}
          >
            {t("trips.newTrip")}
          </Button>
        </CRUDTooltip>
      </div>

      <TripFilters
        themeClasses={themeClasses}
        selectedStatus={selectedStatus}
        searchQuery={searchQuery}
        selectedDeviceId={selectedDeviceId}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onStatusChange={handleStatusFilterChange}
        onSearchChange={handleSearchChange}
        onDeviceChange={handleDeviceFilterChange}
        onDateFromChange={handleDateFromChange}
        onDateToChange={handleDateToChange}
        onClearFilters={handleClearFilters}
        onViewChange={setSelectedView}
        selectedView={selectedView}
        t={tAdmin}
      />

      <TripList
        trips={trips as Trip[]}
        themeClasses={themeClasses}
        onEdit={handleEdit}
        onDetails={handleDetails}
        onAssignments={setAssignmentsTrip}
        onDelete={handleDelete}
        onOpenChat={setChatTrip}
        selectedView={selectedView}
        t={tAdmin}
        loading={loading}
        disableCRUD={disableCRUD}
      />

      {!loading && trips.length > 0 && (
        <TripPagination
          themeClasses={themeClasses}
          currentPage={currentPage}
          totalPages={meta?.totalPages ?? 1}
          totalItems={meta?.totalItems ?? 0}
          onPageChange={setCurrentPage}
          t={tAdmin}
        />
      )}

      {showCreateModal && (
        <TripAddModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          register={register}
          errors={errors}
          loadingSubmit={loadingSubmit}
          onClose={handleCloseModals}
          onSubmit={handleCreateSubmit}
          t={tAdmin}
          setValue={setValue}
          watchedValues={watchedValues}
          showShipments
          hideNotificationFields
        />
      )}

      {showEditModal && selectedTrip && (
        <TripEditModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          register={register}
          errors={errors}
          loadingSubmit={loadingSubmit}
          onClose={handleCloseModals}
          onSubmit={handleEditSubmit}
          onReset={() => handleResetTrip(selectedTrip)}
          t={tAdmin}
          setValue={setValue}
          watchedValues={watchedValues}
          tripStatus={selectedTrip.status?.name}
          selectedTrip={selectedTrip}
          showShipments
          hideNotificationFields
        />
      )}

      {showDetailsModal && selectedTrip && (
        <TripDetailsModal
          trip={selectedTrip}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          onClose={handleCloseModals}
          onEdit={() => {
            handleCloseModals();
            handleEdit(selectedTrip);
          }}
          t={tAdmin}
          disableCRUD={disableCRUD}
        />
      )}

      {assignmentsTrip && (
        <TripAssignmentsModal
          trip={(trips as Trip[]).find((item) => item.id === assignmentsTrip.id) ?? assignmentsTrip}
          themeClasses={themeClasses}
          onClose={() => setAssignmentsTrip(null)}
          t={tAdmin}
        />
      )}

      {chatTrip && (
        <NewConversationModal
          initialMode="viaje"
          initialTripId={chatTrip.id}
          initialParticipantEmails={chatParticipantSeed.emails}
          initialParticipantUserIds={chatParticipantSeed.userIds}
          onClose={() => setChatTrip(null)}
          onCreated={(conversationId) => {
            setChatTrip(null);
            goToConversation(conversationId);
          }}
        />
      )}
    </div>
  );
}
